/**
 * 業務マップの読取契約（設計書 api §4.1 `getBusinessMap`）— ステージ │ 業務フロー │ 業務 │ 業務ルール。
 *
 * **入れ子にせず平らな 4 配列で返す。** 業務は複数の業務フローで、業務ルールは複数の業務で共有
 * されるので、木にすると同じ業務・同じ業務ルールが複製になる。画面は id で引き合わせる。
 *
 * **差分の印は持たない。** ここが返すのは「master」か「master ⊕ 案件」の姿だけである。
 * 追加 / 変更 / 削除 / 配下に変更 の印は案件の変更（`getChangeHistory` の `laneChanges`）
 * から画面が引く。姿と印を同じ応答に混ぜると、印の出どころが 2 本になる。
 *
 * 欄の定義は設計書 api §4.1 が正。ここに書くのは、設計書から決め打ちしたところだけである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップ（ステージ・業務フロー・業務・業務ルールの平らな 4 配列と、ステージの鎖）の入出力契約
 * @graph-connects none
 */
import { z } from 'zod';
import { LocaleInputShape, UntranslatedSchema } from './spec-graph-common.js';
/**
 * 仕様グラフの要素 1 件を指す番号。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 仕様グラフの要素 1 件を指す番号（1 以上の整数）
 * @graph-connects none
 */
export const BusinessMapElementIdSchema = z.number().int().positive();
/**
 * 業務マップが受ける案件の指定。
 *
 * `before` は「その案件を当てる直前の姿」＝ master そのもの、`after` は未確定の変更を
 * 重ねた姿である。案件番号は number で、`AIRCLOSET-139162` の数値部分がそのまま入る。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップが受ける案件の指定（番号と面）
 * @graph-connects none
 */
export const BusinessMapPjSchema = z.object({
    number: z.number().int().positive(),
    side: z.enum(['before', 'after']),
});
/**
 * `getBusinessMap` の入力。案件と表示言語だけを受ける。
 *
 * 絞り込みを持たないのは、レーンの絞り込みが**画面の中の操作**だからである。カードを押す
 * たびに読取を撃ち直すと、絞り込みの往復ごとにグラフ全体を引き直すことになる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `getBusinessMap` の入力（案件・表示言語）
 * @graph-connects none
 */
export const GetBusinessMapInputSchema = z.object({
    pj: BusinessMapPjSchema.optional(),
    ...LocaleInputShape,
});
/**
 * 業務のカードに載る参照 1 件（ドメイン・アクター）。
 *
 * 設計書 api §4.1 はアクターの id の欄を `actorId` と書いているが、ドメインと同じ `id` のまま
 * 運ぶ。案件の変更一覧（F4）のテストがこの形で業務を組んでおり、欄名を変えると F4 の型検査が
 * 落ちるためである。F4 が 4 配列へ移るときに揃える。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務のカードに載る参照 1 件（id と表示名）
 * @graph-connects none
 */
export const BusinessMapRefSchema = z.object({
    id: BusinessMapElementIdSchema,
    label: z.string(),
});
/**
 * ステージの入口・出口（業務と、その結末の行 = 結果の値の用語）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ステージの入口・出口 1 つ（業務の id と結末の行の id）
 * @graph-connects none
 */
export const BusinessMapStageEndSchema = z.object({
    businessId: BusinessMapElementIdSchema,
    endingId: BusinessMapElementIdSchema,
});
/**
 * ステージの鎖 1 本（前のステージの出口を入口に持つステージの並び）。
 *
 * 鎖は保存せず、入口の参照から読取のたびに導出する。`label` は「〈対象〉: 〈最初〉 → 〈最後〉」
 * の組み立てで、材料が欠けていれば空文字になる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップのステージの鎖 1 本（表示名・未翻訳・鎖の順のステージ）
 * @graph-connects none
 */
export const BusinessMapChainSchema = z.object({
    label: z.string(),
    untranslated: UntranslatedSchema,
    stageIds: z.array(BusinessMapElementIdSchema),
});
/**
 * 1 レーン目のステージ 1 件。
 *
 * `entry` が `null` なのは最初のステージと横断ステージ、`prevStageId` が `null` なのは鎖の
 * 前が無いステージである。入口も出口も無いステージが横断ステージになる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップのステージ 1 件（id・表示名・未翻訳・入口・出口・前のステージ・配下の業務フロー）
 * @graph-connects none
 */
export const BusinessMapStageSchema = z.object({
    stageId: BusinessMapElementIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    entry: BusinessMapStageEndSchema.nullable(),
    exit: BusinessMapStageEndSchema.nullable(),
    prevStageId: BusinessMapElementIdSchema.nullable(),
    flowIds: z.array(BusinessMapElementIdSchema),
});
/**
 * 業務フローの型 4 値（主 ／ 入れる ／ 出す ／ 割り込み）。
 *
 * 設計書 api §4.1 は 3 値（割り込みなし）だが、業務フローの型は nodes-edges §1 Flow の 4 値が
 * 正なので 4 値で受ける。3 値に絞ると、割り込み型の業務フローが 1 本在るだけで応答が壊れる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの型 4 値の閉集合
 * @graph-connects none
 */
export const BusinessMapFlowRoleSchema = z.enum(['main', 'inbound', 'outbound', 'interrupt']);
/**
 * 2 レーン目の業務フロー 1 件。
 *
 * `role` が `null` なのは型の欄が読めなかった業務フローである。**主で埋めない** — 主で埋めると、
 * 型の欠けた業務フローが 1 ステージに主フローが 2 本在るように並ぶ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップの業務フロー 1 件（id・属するステージ・表示名・未翻訳・型・含む業務）
 * @graph-connects none
 */
export const BusinessMapFlowSchema = z.object({
    flowId: BusinessMapElementIdSchema,
    stageId: BusinessMapElementIdSchema.nullable(),
    label: z.string(),
    untranslated: UntranslatedSchema,
    role: BusinessMapFlowRoleSchema.nullable(),
    businessIds: z.array(BusinessMapElementIdSchema),
});
/**
 * 3 レーン目の業務 1 件。
 *
 * `ruleIds` が空なのは業務ルールがまだ入っていない業務である（入れ直しで業務を業務ルールより
 * 先に書くので、この形の業務が在る前提で読む）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップの業務 1 件（id・表示名・未翻訳・ドメイン・アクター・業務ルールの集合）
 * @graph-connects none
 */
export const BusinessMapBusinessSchema = z.object({
    businessId: BusinessMapElementIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    domain: BusinessMapRefSchema.nullable(),
    actor: BusinessMapRefSchema.nullable(),
    ruleIds: z.array(BusinessMapElementIdSchema),
});
/**
 * 4 レーン目の業務ルール 1 件。
 *
 * `kind` が `null` なのは形の欄が読めなかった行、`output` が `null` なのは出力の参照が
 * 無い行である。**既定値で埋めない** — 判定表で埋めると、形の欠けた業務ルールが判定表として並ぶ。
 *
 * `output` は省いても通す。業務マップの読取は必ず値（か `null`）を入れて返すが、案件の変更一覧
 * （F4）が案件で消えた業務ルールの仮の行をこの型で組んでおり、その行が欄を持たないためである。
 * F4 が 4 配列へ移ったら省けないようにする。
 *
 * 結末との関わりは持たない — 結末は業務が持つ情報で、業務ルールのカードの札にしない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務マップの業務ルール 1 件（id・表示名・未翻訳・出力・形・実装先）
 * @graph-connects none
 */
export const BusinessMapRuleSchema = z.object({
    ruleId: BusinessMapElementIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    output: z.object({ termId: BusinessMapElementIdSchema, label: z.string() }).nullable().optional(),
    kind: z.string().nullable(),
    implementation: z.array(z.string()),
});
/**
 * `getBusinessMap` の出力。平らな 4 配列とステージの鎖（設計書 api §4.1）。
 *
 * `stages` は鎖の順（鎖ごとに前から、横断ステージは最後）に並ぶ。
 *
 * ページングしない。絞り込みが画面の中の操作なので、続きを取りに戻る形にすると押すたびに
 * 往復が要る。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `getBusinessMap` の出力（ステージ・業務フロー・業務・業務ルールの平らな 4 配列とステージの鎖）
 * @graph-connects none
 */
export const GetBusinessMapOutputSchema = z.object({
    stages: z.array(BusinessMapStageSchema),
    flows: z.array(BusinessMapFlowSchema),
    businesses: z.array(BusinessMapBusinessSchema),
    rules: z.array(BusinessMapRuleSchema),
    chains: z.array(BusinessMapChainSchema),
});
//# sourceMappingURL=spec-graph-business-map.js.map